import { useState } from "react";
import { Loader2, Lock, Mail, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormError } from "@/components/common/ui-bits";
import { Logo } from "@/components/layout/Logo";
import { useAuth } from "@/context/AuthContext";
import { CodeStep } from "@/components/auth/CodeStep";
import { errMsg } from "@/lib/api";

export default function LoginPage() {
  const { startLogin } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) return setError("Invalid email or password");
    setBusy(true);
    try {
      setChallenge(await startLogin(email.trim(), password));
      setPassword("");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <section className="hidden lg:flex flex-col justify-between bg-[#111] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-24 flex flex-col" aria-hidden="true">
          <span className="flex-1 bg-primary" /><span className="flex-1 bg-accent" /><span className="flex-1 bg-primary" />
        </div>
        <div className="absolute inset-y-0 right-0 w-24" style={{ background: "#111", clipPath: "polygon(0 0, 55% 50%, 0 100%)" }} aria-hidden="true" />
        <div className="relative" data-testid="login-brand-logo-slot"><Logo size="lg" tone="dark" /></div>
        <div className="relative max-w-md pr-16">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent mb-4">Finance · Nassau, Bahamas</div>
          <h1 className="font-heading text-5xl font-bold leading-[1.05] tracking-tight">Know where every dollar goes.</h1>
          <p className="mt-5 text-white/70 text-base leading-relaxed">Record money in and money out, and see your totals, profit and budgets at a glance. No accounting know-how needed.</p>
          <div className="mt-10 flex gap-3">
            <span className="rounded-full bg-emerald-400/20 text-emerald-200 px-3 py-1 text-xs font-semibold">Money in</span>
            <span className="rounded-full bg-rose-400/20 text-rose-200 px-3 py-1 text-xs font-semibold">Money out</span>
            <span className="rounded-full bg-white/10 text-white/80 px-3 py-1 text-xs font-semibold">BSD $</span>
          </div>
        </div>
        <div className="text-xs text-white/50 relative">Accounts are created by your shop administrator.</div>
      </section>

      <section className="flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-10 lg:hidden" data-testid="login-logo-slot"><Logo /></div>
          {challenge ? <CodeStep challenge={challenge} onBack={() => setChallenge(null)} /> : (<>
          <h2 className="font-heading text-3xl font-bold tracking-tight">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1.5 mb-8">Use the email and password your administrator gave you.</p>
          <form onSubmit={submit} className="space-y-5" noValidate data-testid="login-form">
            <div>
              <Label htmlFor="email" className="eyebrow">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="h-12 pl-10 rounded-xl text-base" placeholder="you@dalilshop.bs" data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="eyebrow">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-10 rounded-xl text-base" placeholder="••••••••" data-testid="login-password-input" />
              </div>
            </div>
            <FormError message={error} testId="login-error" />
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-xl text-base font-semibold" data-testid="login-submit-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Sign in
            </Button>
          </form>
          </>)}
          <p className="text-xs text-muted-foreground mt-8">Forgot your password? Ask your administrator to reset it.</p>
          <Link to="/install" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-sky-700 hover:underline" data-testid="login-install-link"><Smartphone className="h-4 w-4" />Install the app on your phone</Link>
        </div>
      </section>
    </div>
  );
}
